import { ChangeEvent, KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Modal from "ds/components/Modal";
import ModalCancelButton from "ds/components/Modal/CancelButton";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import ModalHeader from "ds/components/Modal/Header";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";

import { parseFilePath } from "./helpers";
import useFilesNavigation from "./Files/Table/useFilesNavigation";
import useFileNameValidation from "./useFileNameValidation";

type SpaceliftVcsRepoCommitDialogFormFields = {
  fileName: string;
  commitMessage: string;
  commitDescription: string;
};

export type CommitArgs = { filePath?: string; commitMessage: string; commitDescription: string };

type SpaceliftVcsRepoCommitDialogProps = {
  variant: "create" | "edit" | "delete" | "upload";
  isOpen: boolean;
  onClose: () => void;
  onCommit: (args: CommitArgs) => void;
  loading: boolean;
  filePath?: string;
};

const SpaceliftVcsRepoCommitDialog = ({
  variant,
  isOpen,
  onClose,
  onCommit,
  loading,
  filePath,
}: SpaceliftVcsRepoCommitDialogProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const showFileName = variant === "create" || variant === "edit";
  const isDeleting = variant === "delete";

  const { folderPathSegments } = useFilesNavigation();
  const [initialFolderPathSegments, initialFileName] = useMemo(() => {
    if (!showFileName) return [[], ""];

    if (filePath) {
      const { segments, name } = parseFilePath(filePath);
      return [segments, name];
    }

    return [folderPathSegments, ""];
  }, [filePath, folderPathSegments, showFileName]);

  const [pathSegments, setPathSegments] = useState<string[]>(initialFolderPathSegments);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SpaceliftVcsRepoCommitDialogFormFields>({
    defaultValues: {
      fileName: initialFileName,
      commitMessage: "",
      commitDescription: "",
    },
    mode: "onChange",
  });

  const validateFilePath = useFileNameValidation({
    currentFilePath: filePath,
  });

  const handleFileNameChange = (
    e: ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: string) => void
  ) => {
    const value = e.target.value;
    const slashIndex = value.indexOf("/");

    if (slashIndex !== -1) {
      const segment = value.slice(0, slashIndex).trim();
      const rest = value.slice(slashIndex + 1);

      if (segment) {
        setPathSegments((prev) => [...prev, segment]);
      }
      fieldOnChange(rest);
    } else {
      fieldOnChange(value);
    }
  };

  const handleFileNameKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    currentValue: string,
    fieldOnChange: (value: string) => void
  ) => {
    const input = inputRef.current;
    if (
      e.key === "Backspace" &&
      input?.selectionStart === 0 &&
      input?.selectionEnd === 0 &&
      pathSegments.length > 0
    ) {
      e.preventDefault();
      const lastSegment = pathSegments[pathSegments.length - 1];
      setPathSegments((prev) => prev.slice(0, -1));
      fieldOnChange(lastSegment + currentValue);
      requestAnimationFrame(() => {
        input.setSelectionRange(lastSegment.length, lastSegment.length);
      });
    }
  };

  const onSubmit: SubmitHandler<SpaceliftVcsRepoCommitDialogFormFields> = useCallback(
    (data) => {
      const commitMessage = data.commitMessage.trim();
      const commitDescription = data.commitDescription.trim();

      let resolvedFilePath: string | undefined;
      if (showFileName) {
        resolvedFilePath = [...pathSegments, data.fileName.trim()].join("/");
      } else if (filePath) {
        resolvedFilePath = filePath;
      }

      onCommit({ filePath: resolvedFilePath, commitMessage, commitDescription });
    },
    [onCommit, pathSegments, showFileName, filePath]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="default">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader title="Commit changes" onClose={onClose} />
        <ModalContent padding="x-large">
          {isDeleting && (
            <FormField label="File to delete" noMargin>
              {() => (
                <Typography variant="p-body2" tag="p">
                  {filePath}
                </Typography>
              )}
            </FormField>
          )}

          {showFileName && (
            <Controller
              name="fileName"
              control={control}
              rules={{
                validate: (value) => {
                  const fullPath = [...pathSegments, value.trim()].join("/");
                  return validateFilePath(fullPath);
                },
              }}
              render={({ field, fieldState }) => (
                <FormField
                  label="File path"
                  noMargin
                  error={fieldState.error?.message}
                  helperText={
                    pathSegments.length > 0
                      ? "Location: " + pathSegments.join("/") + "/"
                      : 'Type "/" to create folder structure.'
                  }
                >
                  {({ ariaInputProps }) => (
                    <Input
                      ref={inputRef}
                      placeholder="Enter file name here..."
                      value={field.value}
                      error={!!fieldState.error}
                      onChange={(e) => handleFileNameChange(e, field.onChange)}
                      onKeyDown={(e) => handleFileNameKeyDown(e, field.value, field.onChange)}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />
          )}

          <FormField label="Commit message" error={errors?.commitMessage?.message}>
            {({ ariaInputProps }) => (
              <Input
                placeholder="Enter commit message here..."
                error={!!errors?.commitMessage}
                {...register("commitMessage", { required: "Commit message is required" })}
                {...ariaInputProps}
              />
            )}
          </FormField>

          <FormField label="Commit description" isOptional>
            {({ ariaInputProps }) => (
              <Textarea
                placeholder="Enter commit description here..."
                {...register("commitDescription")}
                {...ariaInputProps}
              />
            )}
          </FormField>
        </ModalContent>
        <ModalFooter>
          <ModalCancelButton onClick={onClose} />
          <Button type="submit" variant="primary" disabled={!isValid} loading={loading}>
            Commit changes
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default SpaceliftVcsRepoCommitDialog;
